// httpPorts — the Versioning BC slice (History & Inspection). The event log IS
// the version store: the server folds its retained envelope stream behind the
// `/history` reads. `loadHistory`'s payload is a cross-BC projection (timeline +
// KB triples + grounding coverage); kept as one read.

import { Result } from "@bilda/utils";
import { mkMalformed } from "../externalError.js";
import { buildFileHistoryView, buildHistoryView } from "../historyView.js";
import {
    getEntityHistory,
    getFileHistory,
    getHistory,
    getResourceVersion,
} from "../httpClient.js";
import type { PortResult } from "../ports/result.js";
import type {
    EntityHistoryKind,
    EntityHistoryMoment,
    VersioningPorts,
} from "../ports/versioning.js";
import type { FileHistoryView, HistoryView } from "../ports.js";
import { fields, list, num, str } from "../wireShape.js";
import { command } from "./shared.js";

// The event log IS the version store: the server folds its retained envelope
// stream (whole-log timeline, per-resource version list, fold-to-marker
// reconstruction) behind the `/history` reads. `historyView` lands the wire
// payload in the typed surface shapes; restore reads the fold-to-marker
// post-image, then POSTs the existing `/commands/restoreResourceVersion`
// (read-then-guarded-write, mirroring deleteResource). The inspection lenses
// ("Knows you" / Knowledge bank / Grounding) and per-version diffs read empty
// over HTTP — separate projections not yet exposed; see historyView.ts.
const loadHistory = async (): PortResult<HistoryView> => {
    const r = await getHistory();
    if (Result.isErr(r)) return r;
    return Result.ok(buildHistoryView(r.value));
};

const loadFileHistory = async (
    resourceKey: string,
): PortResult<FileHistoryView> => {
    const r = await getFileHistory(resourceKey);
    if (Result.isErr(r)) return r;
    return Result.ok(buildFileHistoryView(r.value));
};

// Restore composes the fold-to-marker read with the existing restore command:
// GET the reconstructed post-image + head hash, then POST the guarded command.
// `marker` is the version's positional id; `headContentHash` is the freshness
// guard (a concurrent edit refuses as a stale restore, never a clobber).
const restoreResourceVersion = async (
    resourceKey: string,
    marker: string,
): PortResult<void> => {
    const rv = await getResourceVersion(resourceKey, marker);
    if (Result.isErr(rv)) return rv;
    const f = fields(rv.value);
    const restoredContent = str(f?.get("content"));
    const restoredContentHash = str(f?.get("contentHash"));
    const expectedContentHash = str(f?.get("headContentHash"));
    if (
        restoredContent === undefined ||
        restoredContentHash === undefined ||
        expectedContentHash === undefined
    ) {
        return Result.err(
            mkMalformed(
                "reconstructMaterialVersion returned an incomplete post-image",
            ),
        );
    }
    const r = await command("restoreResourceVersion", {
        resourceId: resourceKey,
        restoredFromMarker: Number(marker),
        restoredContent,
        restoredContentHash,
        expectedContentHash,
    });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

// One wire `kind` string → the typed per-entity moment kind, or undefined for
// anything outside the resource-naming milestones (the server's `forResource`
// scope already drops session / fact moments; an unknown/future kind drops
// here too). A typed guard — no cast — mirroring `parseActivityKind`.
const parseEntityHistoryKind = (
    kind: string | undefined,
): EntityHistoryKind | undefined => {
    switch (kind) {
        case "ingested_source":
        case "authored_material":
        case "edited_material":
        case "packaged_material":
        case "deleted_resource":
        case "restored_resource":
        case "version_restored":
            return kind;
        default:
            return undefined;
    }
};

// The per-entity mini-journal: one resource's OWN folded moments. Parse-at-
// boundary — each row's kind / at / eventId must parse; a row missing any of
// them (or carrying an unknown kind) drops (the same posture as every other
// list read). An empty list is a valid ok (honest "no history"); a transport
// error propagates so the pane omits the section rather than fabricating one.
const loadEntityHistory = async (
    resourceKey: string,
): PortResult<readonly EntityHistoryMoment[]> => {
    const r = await getEntityHistory(resourceKey);
    if (Result.isErr(r)) return r;
    const moments: EntityHistoryMoment[] = [];
    for (const raw of list(fields(r.value)?.get("entries"))) {
        const f = fields(raw);
        const kind = parseEntityHistoryKind(str(f?.get("kind")));
        const at = str(f?.get("at"));
        const eventId = str(f?.get("eventId"));
        if (kind === undefined || at === undefined || eventId === undefined) {
            continue;
        }
        const edits = num(f?.get("edits"));
        moments.push(
            edits === undefined
                ? { kind, at, eventId }
                : { kind, at, eventId, edits },
        );
    }
    return Result.ok(moments);
};

export const versioningHttp: VersioningPorts = {
    loadHistory,
    loadFileHistory,
    restoreResourceVersion,
    loadEntityHistory,
};
